import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../api/axios';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { useAuth } from '../../context/AuthContext';

const statusLabels = {
  open: { label: 'Abierto', color: 'badge-info' },
  in_progress: { label: 'En proceso', color: 'badge-warning' },
  completed: { label: 'Completado', color: 'badge-success' },
  cancelled: { label: 'Cancelado', color: 'badge-danger' },
};

const ClientDetail = () => {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [caseFiles, setCaseFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [auditLog, setAuditLog] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const { user } = useAuth();

  const fetchData = async () => {
    try {
      const [clientRes, cfRes] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get('/case-files', { params: { client_id: id } }),
      ]);
      setClient(clientRes.data);
      setCaseFiles(cfRes.data.data || cfRes.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchAuditLog = async () => {
    if (user?.role !== 'notary') return;
    setLoadingAudit(true);
    try {
      const res = await api.get(`/activity-log/clients/${id}`);
      setAuditLog(res.data);
    } catch (error) { console.error(error); }
    finally { setLoadingAudit(false); }
  };

  useEffect(() => { fetchData(); fetchAuditLog(); }, [id]);

  const getStatusBadge = (status) => {
    const badges = { active: 'badge-success', inactive: 'badge-warning', completed: 'badge-secondary' };
    const labels = { active: 'Activo', inactive: 'Inactivo', completed: 'Completado' };
    return <span className={`badge ${badges[status]}`}>{labels[status]}</span>;
  };

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>;
  if (!client) return <div className="alert alert-danger">Cliente no encontrado</div>;

  return (
    <div className="container-fluid">
      <Breadcrumb items={[
        { label: 'Clientes', to: '/clients' },
        { label: client.name },
      ]} />

      {/* Header */}
      <div className="page-header d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0" style={{ wordBreak: 'break-word' }}>{client.name}</h4>
        <div className="d-flex flex-wrap" style={{ gap: '8px', flexShrink: 0, marginLeft: '12px' }}>
          <Link to={`/clients/${id}/edit`} className="btn btn-outline-secondary btn-sm">
            <i className="ti-pencil mr-1"></i> Editar
          </Link>
          <Link to="/clients" className="btn btn-outline-secondary btn-sm">
            <i className="ti-arrow-left mr-1"></i> Regresar
          </Link>
        </div>
      </div>

      <div className="row">
        {/* Panel lateral */}
        <div className="col-12 col-md-4">

          {/* Aviso desactivado — primero si aplica */}
          {!client.active && (
            <div className="card mb-3" style={{ borderLeft: '4px solid #EF4444' }}>
              <div className="card-body py-3">
                <div className="d-flex align-items-start">
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%', backgroundColor: '#FEE2E2',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, marginRight: 12
                  }}>
                    <svg style={{ width: 16, height: 16, color: '#EF4444' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                  </div>
                  <div>
                    <p className="mb-1 font-weight-bold text-danger" style={{ fontSize: '13px' }}>Cliente Desactivado</p>
                    <p className="mb-0 text-muted" style={{ fontSize: '13px' }}>
                      {client.deactivation_reason || 'Sin motivo registrado'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Info del cliente */}
          <div className="card mb-3">
            <div className="card-header"><h6 className="mb-0">Información del Cliente</h6></div>
            <div className="card-body">
              <table className="table table-sm table-borderless mb-0">
                <tbody>
                  <tr><td className="text-muted">Estado</td><td>{getStatusBadge(client.status)}</td></tr>
                  <tr><td className="text-muted">Teléfono</td><td>{client.phone || '-'}</td></tr>
                  <tr>
                    <td className="text-muted">Email</td>
                    <td style={{ wordBreak: 'break-all' }}>{client.email || '-'}</td>
                  </tr>
                  <tr><td className="text-muted">Dirección</td><td>{client.address || '-'}</td></tr>
                  <tr><td className="text-muted">Responsable</td><td>{client.assigned_user?.name || '-'}</td></tr>
                  <tr>
                    <td className="text-muted">Registro</td>
                    <td>{new Date(client.created_at).toLocaleDateString('es-MX')}</td>
                  </tr>
                </tbody>
              </table>
              <hr />
              <p className="text-muted small mb-1">Motivo</p>
              <p className="mb-0">{client.reason}</p>
            </div>
          </div>

          {/* Contacto */}
          {client.contact_name && (
            <div className="card mb-3">
              <div className="card-header d-flex align-items-center">
                <h6 className="mb-0">Contacto</h6>
                {client.contact_role && (
                  <span className="badge badge-info ml-2">{client.contact_role}</span>
                )}
              </div>
              <div className="card-body">
                <table className="table table-sm table-borderless mb-0">
                  <tbody>
                    <tr><td className="text-muted">Nombre</td><td><strong>{client.contact_name}</strong></td></tr>
                    {client.contact_phone && (
                      <tr><td className="text-muted">Teléfono</td><td>{client.contact_phone}</td></tr>
                    )}
                    {client.contact_email && (
                      <tr>
                        <td className="text-muted">Email</td>
                        <td style={{ wordBreak: 'break-all' }}>{client.contact_email}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Expedientes */}
        <div className="col-12 col-md-8">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                Expedientes <span className="badge badge-secondary ml-1">{caseFiles.length}</span>
              </h6>
              <Link to={`/clients/${id}/case-files/create`} className="btn btn-primary btn-sm">
                <i className="ti-plus mr-1"></i> Nuevo
              </Link>
            </div>
            <div className="card-body p-0 mobile-card-list">
              {caseFiles.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <i className="ti-folder" style={{ fontSize: '2rem' }}></i>
                  <p className="mt-2">No hay expedientes registrados</p>
                </div>
              ) : (
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Título</th>
                      <th className="mobile-hide">Trámite</th>
                      <th className="mobile-hide">Responsable</th>
                      <th className="mobile-hide">Docs</th>
                      <th>Tiempo</th>
                      <th>Estado</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {caseFiles.map((cf) => (
                      <tr key={cf.id} style={{ backgroundColor: cf.is_stale ? '#FFF8F0' : 'inherit' }}>
                        {/* Título — siempre visible */}
                        <td className="td-name">
                          {cf.title}
                          {/* En móvil mostramos trámite debajo */}
                          <div className="d-md-none" style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                            {cf.procedure_type?.name}
                          </div>
                        </td>
                        <td className="mobile-hide">{cf.procedure_type?.name || '-'}</td>
                        <td className="mobile-hide">{cf.assigned_user?.name || '-'}</td>
                        <td className="mobile-hide">
                          <span className={`badge ${cf.document_stats?.delivered === cf.document_stats?.total && cf.document_stats?.total > 0 ? 'badge-success' : 'badge-warning'}`}>
                            {cf.document_stats?.delivered}/{cf.document_stats?.total}
                          </span>
                        </td>
                        <td data-label="Tiempo">
                          <span className={`badge ${cf.is_stale ? 'badge-danger' : 'badge-secondary'}`}>
                            {cf.elapsed_time}
                          </span>
                        </td>
                        <td data-label="Estado">
                          <span className={`badge ${statusLabels[cf.status]?.color}`}>
                            {statusLabels[cf.status]?.label}
                          </span>
                        </td>
                        <td className="td-actions">
                          <Link to={`/case-files/${cf.id}`} className="btn btn-sm btn-outline-primary">
                            <i className="ti-eye"></i>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
        {user?.role === 'notary' && (
          <div className="col-12 mt-4">
            <div className="card">
              <div className="card-header">
                <h6 className="mb-0">Log de Auditoría <span className="badge badge-secondary ml-1">{auditLog.length}</span></h6>
              </div>
              <div className="card-body p-0" style={{ overflowX: 'auto' }}>
                {loadingAudit ? (
                  <div className="text-center py-4"><div className="spinner-border text-primary"></div></div>
                ) : auditLog.length === 0 ? (
                  <div className="text-center text-muted py-4">No hay registros de auditoría</div>
                ) : (
                  <table className="table table-hover mb-0" style={{ tableLayout: 'fixed' }}>
                    <thead>
                      <tr>
                        <th style={{ width: '12%' }}>Acción</th>
                        <th style={{ width: '18%' }}>Por</th>
                        <th style={{ width: '40%' }}>Cambios</th>
                        <th style={{ width: '18%' }}>Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLog.map(log => (
                        <tr key={log.id}>
                          <td>
                            <span className={`badge ${log.event === 'created' ? 'badge-success' : log.event === 'deleted' ? 'badge-danger' : 'badge-info'}`}>
                              {log.event === 'created' ? 'Creado' : log.event === 'deleted' ? 'Eliminado' : 'Editado'}
                            </span>
                          </td>
                          <td><small>{log.causer}</small></td>
                          <td style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                            {log.old && log.new ? (
                              <div style={{ fontSize: '11px' }}>
                                {Object.keys(log.new).map(key => (
                                  log.old[key] !== log.new[key] ? (
                                    <div key={key} className="mb-1">
                                      <span className="text-muted">{key}:</span>{' '}
                                      <span className="text-danger" style={{ textDecoration: 'line-through' }}>{String(log.old[key] ?? '—')}</span>
                                      {' → '}
                                      <span className="text-success">{String(log.new[key] ?? '—')}</span>
                                    </div>
                                  ) : null
                                ))}
                              </div>
                            ) : (
                              <small className="text-muted">{log.description}</small>
                            )}
                          </td>
                          <td>
                            <small className="text-muted">
                              {new Date(log.created_at).toLocaleDateString('es-MX', {
                                day: '2-digit', month: 'short', year: 'numeric',
                                hour: '2-digit', minute: '2-digit'
                              })}
                            </small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientDetail;